import { ReactNode, useMemo, useState } from "react";
import { GraduationCap, LayoutDashboard, Menu, Search, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/Navigation";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { CommandPalette } from "./CommandPalette";
import { useCommandPalette } from "./useCommandPalette";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { adminSidebarConfig } from "./adminSidebarConfig";
import { instructorSidebarConfig } from "./instructorSidebarConfig";
import {
  SidebarShell,
  useSidebarCollapsed,
  type SidebarVariant,
} from "./SidebarShell";
import {
  buildUserSidebar,
  type SidebarSectionConfig,
} from "./userSidebarConfig";

interface DashboardShellProps {
  variant?: SidebarVariant;
  children?: ReactNode;
  sections?: SidebarSectionConfig[];
}

/**
 * Persistent dashboard chrome used by the route layout.
 * The shell stays mounted while only the content pane changes.
 */
export function DashboardShell({ variant, children, sections }: DashboardShellProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isAdmin, isInstructor } = useUserRole();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();

  // Badge variant follows role precedence: admin > instructor > user.
  const resolvedVariant: SidebarVariant = useMemo(() => {
    if (variant) return variant;
    if (isAdmin) return "admin";
    if (isInstructor) return "instructor";
    return "user";
  }, [isAdmin, isInstructor, variant]);

  // Sections are merged from EVERY role the user holds, in order:
  // admin → instructor → user. So an admin who is also a user sees the full
  // admin sidebar followed by their personal sections, never losing access.
  const resolvedSections = useMemo<SidebarSectionConfig[]>(() => {
    if (sections?.length) return sections;
    if (variant === "admin") return adminSidebarConfig;
    if (variant === "instructor") return instructorSidebarConfig;

    const merged: SidebarSectionConfig[] = [];

    // Multi-role users (admin and/or instructor) get a tiny "Workspace"
    // group at the very top so context switching between Admin overview /
    // Teaching overview / My dashboard is a single, obvious click. Pure
    // learners don't see this group — their sidebar already starts with
    // /dashboard.
    if (isAdmin || isInstructor) {
      const workspaceItems = [];
      if (isAdmin) {
        workspaceItems.push({
          to: "/admin",
          icon: Shield,
          labelKey: "sidebar.workspace.admin",
          end: true,
        });
      }
      if (isInstructor) {
        workspaceItems.push({
          to: "/instructor/dashboard",
          icon: GraduationCap,
          labelKey: "sidebar.workspace.teaching",
          end: true,
        });
      }
      workspaceItems.push({
        to: "/dashboard",
        icon: LayoutDashboard,
        labelKey: "sidebar.workspace.personal",
        end: true,
      });
      merged.push({
        labelKey: "sidebar.sections.workspace",
        items: workspaceItems,
      });
    }

    if (isAdmin) merged.push(...adminSidebarConfig);
    if (isInstructor) merged.push(...instructorSidebarConfig);
    merged.push(...buildUserSidebar());
    return merged;
  }, [isAdmin, isInstructor, sections, variant]);

  const hasAuthenticatedShell = Boolean(user);

  return (
    <>
      {/* Shell occupies the viewport. Sidebar + topbar are physically pinned
          inside it, and only the right content column scrolls. This makes
          "sidebar stays visible while scrolling" a structural guarantee
          instead of relying on `position: sticky` (which is silently
          disabled by any ancestor `overflow` rule). */}
      <div className="h-screen w-full overflow-hidden bg-background text-foreground">
        <div className="flex h-full w-full">
          {hasAuthenticatedShell && (
            <div className="hidden h-full shrink-0 lg:block">
              <SidebarShell
                variant={resolvedVariant}
                sections={resolvedSections}
                collapsed={collapsed}
                onToggle={toggleCollapsed}
              />
            </div>
          )}

          <div className="flex h-full min-w-0 flex-1 flex-col">
            <header
              className={cn(
                "relative shrink-0 z-40",
                "border-b glass-divider",
                "bg-background/70 backdrop-blur-2xl",
                "supports-[backdrop-filter]:bg-background/60"
              )}
            >
              {/* gradient hairline */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
              />
              <div className="flex h-14 sm:h-15 items-center gap-2 sm:gap-3 px-3 sm:px-5 lg:px-7">
                {hasAuthenticatedShell && (
                  <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label={t("sidebar.expand")}
                    className={cn(
                      "lg:hidden flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      "border glass-border glass-surface text-foreground/80",
                      "hover:[background-color:hsl(var(--glass-tint)/0.08)] hover:text-foreground transition-colors"
                    )}
                  >
                    <Menu className="h-[18px] w-[18px]" />
                  </button>
                )}

                {hasAuthenticatedShell && (
                  <>
                    {/* Mobile: icon-only search button */}
                    <button
                      type="button"
                      onClick={() => setPaletteOpen(true)}
                      aria-label={t("commandPalette.placeholder")}
                      className={cn(
                        "md:hidden flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                        "border glass-border glass-surface text-foreground/80",
                        "hover:[background-color:hsl(var(--glass-tint)/0.08)] hover:text-foreground transition-colors"
                      )}
                    >
                      <Search className="h-[18px] w-[18px]" />
                    </button>

                    {/* Tablet/Desktop: premium search pill */}
                    <button
                      type="button"
                      onClick={() => setPaletteOpen(true)}
                      className={cn(
                        "hidden md:inline-flex items-center gap-2.5 h-9 rounded-xl",
                        "border glass-border glass-surface hover:[background-color:hsl(var(--glass-tint)/0.08)]",
                        "text-foreground/60 hover:text-foreground/90",
                        "transition-all duration-200",
                        "px-3 md:w-[220px] lg:w-[300px] xl:w-[360px]",
                        "shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.04)]",
                        "hover:shadow-[0_0_0_1px_hsl(var(--primary)/0.25),inset_0_1px_0_0_hsl(0_0%_100%/0.06)]"
                      )}
                    >
                      <Search className="h-4 w-4 shrink-0 text-foreground/50" />
                      <span className="flex-1 truncate text-left text-[13px]">
                        {t("commandPalette.placeholder")}
                      </span>
                      <kbd
                        className={cn(
                          "shrink-0 inline-flex items-center gap-0.5 h-5 rounded-md",
                          "border glass-border [background-color:hsl(var(--glass-tint)/0.06)] px-1.5",
                          "font-mono text-[10px] font-medium text-foreground/55"
                        )}
                      >
                        ⌘K
                      </kbd>
                    </button>
                  </>
                )}

                <div className="min-w-0 flex-1">
                  <Navigation compact />
                </div>

                {hasAuthenticatedShell && <CartDrawer />}
              </div>
            </header>

            {/* Sole scroll container for dashboard pages.
                `data-shell-scroll` lets child pages (e.g. sticky filter
                bars) target it explicitly if needed. */}
            <main
              data-shell-scroll
              className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 lg:px-8 lg:py-8"
            >
              {children}
            </main>
          </div>
        </div>
      </div>

      {hasAuthenticatedShell && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            className="w-[284px] border-r border-border/60 bg-background/95 p-0 backdrop-blur-2xl"
          >
            <SheetTitle className="sr-only">Dashboard navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Navigate between your account, learning, and admin sections.
            </SheetDescription>
            <SidebarShell
              variant={resolvedVariant}
              sections={resolvedSections}
              collapsed={false}
              onToggle={() => undefined}
              onNavigate={() => setMobileOpen(false)}
              inSheet
            />
          </SheetContent>
        </Sheet>
      )}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
